<script setup lang="ts">
// biome-ignore lint/correctness/noUnusedImports: used via tag <UiPopup> in <template>
import { UiPopup } from '@/ui-kit';
import {
  PAYMENT_FAILURE_CAPTION_KEYS,
  type PaymentFailureCaptionKey,
} from './paymentFailureCaption';
import type { PaymentTerminalState } from './paymentTerminalTypes';

interface Props {
  errorCaptionKey?: PaymentFailureCaptionKey;
  isFinal?: boolean;
  showCancelledPopup: boolean;
  state: PaymentTerminalState;
  total: string;
}

withDefaults(defineProps<Props>(), {
  errorCaptionKey: PAYMENT_FAILURE_CAPTION_KEYS.generic,
  isFinal: false,
});

// biome-ignore lint/correctness/noUnusedVariables: used in <template>
const emit = defineEmits<{
  cancel: [];
  exitSession: [];
  popupContinue: [];
  popupExit: [];
  retry: [];
}>();
</script>

<template>
  <div
    class="PaymentTerminal"
    data-testid="payment-terminal-root"
    :data-state="state"
  >
    <div v-if="state === 'waiting'">
      <h1 class="kiosk-title">{{ $t('PaymentTerminal.title') }}</h1>
      <div
        class="PaymentTerminal__spinner kiosk-spinner"
        data-testid="payment-terminal-spinner"
      />
      <p class="kiosk-caption">
        {{ $t('PaymentTerminal.total') }} {{ total }}
      </p>
      <p class="kiosk-caption">
        {{ $t('PaymentTerminal.captionPayment') }}
      </p>

      <div class="PaymentTerminal__footer">
        <button
          class="kiosk-button"
          data-testid="payment-terminal-cancel"
          @click="emit('cancel')"
        >
          {{ $t('PaymentTerminal.buttonCancel') }}
        </button>
      </div>
    </div>

    <div v-if="state === 'success'">
      <h1 class="kiosk-title">
        {{ $t('PaymentTerminal.titlePaymentSuccess') }}
      </h1>
      <div
        class="PaymentTerminal__spinner kiosk-spinner"
        data-testid="payment-terminal-spinner"
      />
      <p class="kiosk-caption">
        {{ $t('PaymentTerminal.total') }} {{ total }}
      </p>
      <p class="kiosk-caption">
        {{ $t('PaymentTerminal.captionPrinting') }}
      </p>
    </div>

    <div v-if="state === 'error'">
      <h1 class="kiosk-title">
        {{ $t('PaymentTerminal.titlePaymentError') }}
      </h1>
      <p
        class="kiosk-caption"
        data-testid="payment-terminal-error-caption"
        :data-caption-key="errorCaptionKey"
      >
        {{ $t(errorCaptionKey) }}
      </p>

      <div class="PaymentTerminal__footer">
        <button
          v-if="!isFinal"
          class="kiosk-button"
          data-testid="payment-terminal-retry"
          @click="emit('retry')"
        >
          {{ $t('PaymentTerminal.buttonRetry') }}
        </button>
        <button
          :class="isFinal ? 'kiosk-button' : 'kiosk-button-inline'"
          data-testid="payment-terminal-exit"
          @click="emit('exitSession')"
        >
          {{ $t('PaymentTerminal.buttonExitSession') }}
        </button>
      </div>
    </div>
  </div>

  <UiPopup
    v-if="showCancelledPopup"
    class="PaymentTerminal__popup"
    @close="emit('popupContinue')"
  >
    <div
      class="PaymentTerminal__popup-content"
      data-testid="payment-terminal-cancelled-popup"
    >
      <h1 class="kiosk-title">
        {{ $t('PaymentTerminal.popupCancelledTitle') }}
      </h1>
      <p class="kiosk-caption">
        {{ $t('PaymentTerminal.popupCancelledCaption') }}
      </p>

      <div class="PaymentTerminal__popup-actions">
        <button
          class="kiosk-button-inline"
          data-testid="payment-terminal-popup-exit"
          @click="emit('popupExit')"
        >
          {{ $t('PaymentTerminal.buttonExitSession') }}
        </button>
        <button
          class="kiosk-button"
          data-testid="payment-terminal-popup-continue"
          @click="emit('popupContinue')"
        >
          {{ $t('PaymentTerminal.popupButtonContinue') }}
        </button>
      </div>
    </div>
  </UiPopup>
</template>

<style scoped lang="scss">
.PaymentTerminal {
  max-width: 55rem;
  text-align: center;
}

.PaymentTerminal__spinner {
  --kiosk-spinner-size: 5rem;
  --kiosk-spinner-color: var(--Kiosk--font_color);
  --kiosk-spinner-opacity: 0.4;
  --kiosk-spinner-thinkness: 0.125;

  width: var(--kiosk-spinner-size);
  height: var(--kiosk-spinner-size);
  margin: 3.5rem auto 2.5rem;
  position: relative;
}

.PaymentTerminal__footer {
  margin-top: 3rem;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: var(--payment-terminal-footer-direction, row);
  gap: var(--payment-terminal-footer-gap, 1.5rem);
  flex-wrap: wrap;
}

.PaymentTerminal__popup-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 1rem;
}

.PaymentTerminal__popup-actions {
  display: flex;
  flex-direction: var(--payment-terminal-popup-actions-direction, row);
  align-items: center;
  margin-top: 2.5rem;
  gap: var(--payment-terminal-popup-actions-gap, 2rem);
}
</style>
